import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import AccountStep, { type AccountData } from './steps/AccountStep';
import FeatureSelectionStep, { type FeatureData } from './steps/FeatureSelectionStep';
import WifiSetupStep, { type WifiData } from './steps/WifiSetupStep';
import SurveySetupStep, { type SurveyData } from './steps/SurveySetupStep';
import MaterialGenerationStep, { type MaterialData } from './steps/MaterialGenerationStep';
import { createVenue } from '../../utils/venueApi';
import Button from '../ui/Button';
import Card from '../ui/Card';

const SESSION_KEY = 'whobrew_venue_onboarding';

const STEPS = [
  { label: 'Business', number: 1, title: 'Business basics' },
  { label: 'Features', number: 2, title: 'Choose features' },
  { label: 'Wi-Fi', number: 3, title: 'Wi-Fi setup' },
  { label: 'Survey', number: 4, title: 'Survey setup' },
  { label: 'Materials', number: 5, title: 'QR materials' },
];

export interface OnboardingState {
  step: number;
  account: AccountData;
  features: FeatureData;
  wifi: WifiData;
  survey: SurveyData;
  material: MaterialData;
}

function getDefaultState(email: string): OnboardingState {
  return {
    step: 1,
    account: {
      name: '',
      businessType: 'coffee_shop',
      businessTypeOther: '',
      email,
      address: '',
      logoFile: null,
      logoPreview: '',
    },
    features: {
      wifi: true,
      menu: false,
      punchCard: false,
      reviewFunnel: false,
      specials: false,
      reservations: false,
      socialLinks: false,
    },
    wifi: { ssid: '', password: '', rotateReminder: false },
    survey: { enabled: false, questions: [] },
    material: { nfcInterest: false },
  };
}

function loadFromSession(email: string): OnboardingState {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as OnboardingState;
      parsed.account.logoFile = null;
      return parsed;
    }
  } catch {
    // Ignore parse errors
  }
  return getDefaultState(email);
}

function saveToSession(state: OnboardingState) {
  try {
    const toSave = {
      ...state,
      account: { ...state.account, logoFile: null },
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(toSave));
  } catch {
    // sessionStorage may be full or unavailable
  }
}

const VenueOnboarding: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';

  const [state, setState] = useState<OnboardingState>(() => loadFromSession(userEmail));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (userEmail && !state.account.email) {
      setState((prev) => ({
        ...prev,
        account: { ...prev.account, email: userEmail },
      }));
    }
  }, [userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    saveToSession(state);
  }, [state]);

  useEffect(() => {
    document.title = 'Venue Setup - WhoBrew';
  }, []);

  const updateState = useCallback((partial: Partial<OnboardingState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const goToStep = (step: number) => {
    setState((prev) => ({ ...prev, step }));
    window.scrollTo(0, 0);
  };

  const handleComplete = async () => {
    if (!user) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        userId: user.id,
        name: state.account.name,
        businessType: state.account.businessType,
        businessTypeOther: state.account.businessType === 'other' ? state.account.businessTypeOther : undefined,
        email: state.account.email,
        address: state.account.address || undefined,
        features: state.features,
        wifi: {
          ssid: state.wifi.ssid,
          password: state.wifi.password,
          rotateReminder: state.wifi.rotateReminder,
        },
        survey: state.survey.enabled
          ? { enabled: true, questions: state.survey.questions }
          : { enabled: false, questions: [] },
        nfcInterest: state.material.nfcInterest,
      };

      await createVenue(payload as any);
      sessionStorage.removeItem(SESSION_KEY);
      navigate('/venue/dashboard');
    } catch (err: any) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const previewSlug = state.account.name
    ? state.account.name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-').toLowerCase().slice(0, 30)
    : '';

  const currentStepData = STEPS[state.step - 1];

  return (
    <div className="min-h-screen bg-white text-coffee-900">
      {/* Page header */}
      <div className="border-b border-coffee-100 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="eyebrow mb-1.5">Venue Setup</div>
          <h1 className="text-display text-3xl sm:text-4xl text-coffee-900">
            {currentStepData.title}
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress bar */}
        <nav aria-label="Onboarding progress" className="mb-8">
          <div className="flex items-center gap-0">
            {STEPS.map((step, i) => {
              const isActive = state.step === step.number;
              const isComplete = state.step > step.number;
              return (
                <React.Fragment key={step.number}>
                  {i > 0 && (
                    <div
                      className={`flex-1 h-0.5 ${isComplete ? 'bg-coffee-600' : 'bg-coffee-100'}`}
                    />
                  )}
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${
                        isComplete
                          ? 'bg-coffee-600 text-white'
                          : isActive
                          ? 'bg-coffee-600 text-white ring-4 ring-coffee-100'
                          : 'bg-coffee-50 border border-coffee-200 text-coffee-400'
                      }`}
                      aria-current={isActive ? 'step' : undefined}
                    >
                      {isComplete ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        step.number
                      )}
                    </div>
                    <span
                      className={`text-[10px] mt-1 hidden sm:block tracking-eyebrow font-semibold ${
                        isActive ? 'text-coffee-700' : isComplete ? 'text-coffee-500' : 'text-coffee-300'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 ${isComplete ? 'bg-coffee-600' : 'bg-coffee-100'}`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </nav>

        {/* Step content card */}
        <Card elevated padded={false} className="p-7">
          {state.step === 1 && (
            <AccountStep
              data={state.account}
              onUpdate={(account) => updateState({ account })}
              onNext={() => goToStep(2)}
            />
          )}
          {state.step === 2 && (
            <FeatureSelectionStep
              data={state.features}
              onUpdate={(features) => updateState({ features })}
              onNext={() => goToStep(3)}
              onBack={() => goToStep(1)}
            />
          )}
          {state.step === 3 && (
            <WifiSetupStep
              data={state.wifi}
              onUpdate={(wifi) => updateState({ wifi })}
              onNext={() => goToStep(4)}
              onBack={() => goToStep(2)}
            />
          )}
          {state.step === 4 && (
            <SurveySetupStep
              data={state.survey}
              onUpdate={(survey) => updateState({ survey })}
              onNext={() => goToStep(5)}
              onBack={() => goToStep(3)}
            />
          )}
          {state.step === 5 && (
            <MaterialGenerationStep
              data={state.material}
              onUpdate={(material) => updateState({ material })}
              onBack={() => goToStep(4)}
              onComplete={handleComplete}
              submitting={submitting}
              error={submitError}
              businessName={state.account.name}
              ssid={state.wifi.ssid}
              slug={previewSlug}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default VenueOnboarding;
